import { Component, ElementRef, Signal, WritableSignal, afterNextRender, inject, signal, viewChild } from '@angular/core';
import { AbstractControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { NgTemplateOutlet } from '@angular/common';
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { ButtonCtaComponent } from '@components/button-cta/button-cta.component';
import { AddressFieldErrors } from '@shared/address-form/address-field-validators';
import { AutofillFocus } from '../services/address-autofill.service';

export enum RemainingDetailsSheetResult { Save = 'save' }

export enum AddressFieldErrorType {
  NonEnglish = 'nonEnglish',
  SpecialCharacters = 'specialCharacters',
  MinCharLimit = 'minCharLimit',
}

export type RemainingDetailsFieldErrors = AddressFieldErrors;

export interface RemainingDetailsSheetData {
  form: FormGroup;
  fieldErrors: RemainingDetailsFieldErrors;
  focus: AutofillFocus;
}

@Component({
  selector: 'app-remaining-details-sheet',
  templateUrl: './remaining-details-sheet.component.html',
  imports: [ReactiveFormsModule, NgTemplateOutlet, ButtonCtaComponent],
})
export class RemainingDetailsSheetComponent {
  private readonly sheetRef: MatBottomSheetRef = inject(MatBottomSheetRef);
  protected readonly data: RemainingDetailsSheetData = inject(MAT_BOTTOM_SHEET_DATA);
  protected readonly submitted: WritableSignal<boolean> = signal(false);
  protected readonly ErrorType: typeof AddressFieldErrorType = AddressFieldErrorType;

  private readonly buildingInput: Signal<ElementRef<HTMLInputElement>> = viewChild.required<ElementRef<HTMLInputElement>>('buildingInput');
  private readonly localityInput: Signal<ElementRef<HTMLInputElement>> = viewChild.required<ElementRef<HTMLInputElement>>('localityInput');

  constructor() {
    afterNextRender((): void => this.focus(this.data.focus));
  }

  protected get showBuildingRequired(): boolean {
    return this.submitted() && !this.control('buildingDetails').value;
  }

  protected get showLocalityRequired(): boolean {
    return this.submitted() && !this.control('localityDetails').value;
  }

  protected get buildingErrorType(): string {
    const error = this.data.fieldErrors.buildingDetails;
    return this.control('buildingDetails').value && error.buildingDetailsError ? error.errorType : '';
  }

  protected get localityErrorType(): string {
    const error = this.data.fieldErrors.localityDetails;
    return this.control('localityDetails').value && error.localityDetailsError ? error.errorType : '';
  }

  protected get landmarkErrorType(): string {
    const error = this.data.fieldErrors.landmark;
    return error.landmarkError ? error.errorType : '';
  }

  protected save(): void {
    this.submitted.set(true);
    if (!this.isFilledAndValid('buildingDetails') || this.data.fieldErrors.buildingDetails.buildingDetailsError) {
      this.focus(AutofillFocus.Building);
      return;
    }
    if (!this.isFilledAndValid('localityDetails') || this.data.fieldErrors.localityDetails.localityDetailsError) {
      this.focus(AutofillFocus.Area);
      return;
    }
    if (!this.control('landmark').valid || this.data.fieldErrors.landmark.landmarkError) return;
    this.sheetRef.dismiss(RemainingDetailsSheetResult.Save);
  }

  async handleBackPress(): Promise<boolean> {
    this.sheetRef.dismiss();
    return true;
  }

  private control(name: string): AbstractControl {
    return this.data.form.controls[name];
  }

  private isFilledAndValid(name: string): boolean {
    const control = this.control(name);
    return !!control.value && control.valid;
  }

  private focus(target: AutofillFocus): void {
    const input = target === AutofillFocus.Area ? this.localityInput() : this.buildingInput();
    input.nativeElement.focus();
  }
}
